import Image from "next/image";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Dropdown, DropdownItem } from "staysure-component-library";

import {
  TRAVELING_TO_VARIATION,
  UK_COUNTRY_NAME,
  UK_QUOTE_URL,
} from "@/constants";
import { RootState } from "@/store/store";
import { travelFromCountryData } from "@/utils/question-location-from.utils";

import classes from "./location-from-container.module.css";
import styles from "../../../question.module.css";
import { useLocationTo } from "../../provider/location-to-provider";
import { LocationFromContainerProps } from "../../type/location-from.type";
import UKRedirectModal from "../uk-redirect-modal";

function LocationFromContainer({
  questionOneText,
  questionOnePlaceHolder,
  setFromCountry,
  locationFromCountreyClick,
}: Readonly<LocationFromContainerProps>) {
  const question = useSelector((state: RootState) => state.quote);
  const fromCountry = question?.attributes.tripDetails?.fromLocation;
  const [travellingFrom, setTravellingFrom] = useState<string | undefined>("");
  const { setSelectedFrom } = useLocationTo();

  // UK redirect modal state
  const [showUKModal, setShowUKModal] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);

  // Check if UK should be shown in dropdown
  const shouldShowUKInDropdown = React.useMemo(() => {
    if (!globalThis.window) return false;
    const travelingToVariationOne = localStorage.getItem(
      TRAVELING_TO_VARIATION
    );
    const currentBrand = globalThis.window.brand;
    return travelingToVariationOne === "true" && currentBrand === "expat";
  }, []);

  // Filter countries to conditionally include UK
  const availableCountries = React.useMemo(() => {
    if (shouldShowUKInDropdown) {
      return travelFromCountryData;
    }
    return travelFromCountryData.filter(
      (country) => country.displayName !== UK_COUNTRY_NAME
    );
  }, [shouldShowUKInDropdown]);

  useEffect(() => {
    if (fromCountry && fromCountry !== "") {
      const selectedCountry = travelFromCountryData.find(
        (country) => country.code === fromCountry
      );
      if (selectedCountry) {
        setTravellingFrom(selectedCountry?.displayName);
        setSelectedFrom(selectedCountry?.displayName);
      } else {
        setTravellingFrom("");
        setSelectedFrom("");
      }
    }
  }, [fromCountry, setSelectedFrom]);

  const handleCountryChange = useCallback(
    (selectedCountry: string) => {
      // Check if UK is selected and should show modal
      if (selectedCountry === UK_COUNTRY_NAME && globalThis.window) {
        const travelingToVariationOne = localStorage.getItem(
          TRAVELING_TO_VARIATION
        );
        const currentBrand = globalThis.window.brand;

        const shouldShowUKModal =
          travelingToVariationOne === "true" && currentBrand === "expat";

        if (shouldShowUKModal) {
          setShowUKModal(true);
          return;
        }
      }

      setTravellingFrom(selectedCountry);
      setSelectedFrom(selectedCountry);
      setFromCountry(selectedCountry);
    },
    [setSelectedFrom, setFromCountry]
  );

  const handleUKModalConfirm = () => {
    setShowUKModal(false);
    // Redirect to UK quote site
    if (globalThis.window) {
      globalThis.window.location.href = UK_QUOTE_URL;
    }
  };

  const handleUKModalCancel = () => {
    setShowUKModal(false);
    // Reset selection and force dropdown to re-render
    setTravellingFrom("");
    setSelectedFrom("");
    setFromCountry("");
    setDropdownKey((prev) => prev + 1);
  };

  const isInitialRender = useRef(true);

  const renderQuestionTwoSection = () => {
    return (
      <div className=" w-full" data-testid="renderQuestionTwoSection-test-id">
        <div className=" ">
          <h1
            className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px]
                md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
          >
            {questionOneText ?? "Where are you travelling from???"}
          </h1>
          <div
            className={` ${styles.country_wrapper} lg:w-[414px] md:w-[400px] w-full mx-auto md:h-[56px] mt-spacing-l`}
          >
            <Dropdown
              key={dropdownKey}
              id="country-select-dropdown"
              isDispTextValueAfterSelect
              defaultValue={travellingFrom}
              enableOpenDelay
              openDelayMs={450}
              onSelectionChange={(value: string) => {
                if (!travellingFrom || !isInitialRender.current) {
                  locationFromCountreyClick?.();
                } else {
                  isInitialRender.current = false;
                }
                handleCountryChange(value);
              }}
              placeholderText={questionOnePlaceHolder ?? "Select a country"}
              className={`${classes.dropdown_btn} py-spacing-m py px-spacing-m flex space-between w-full`}
              listBoxStyle={{
                outline: "none",
                margin: "0px 0px 0px 0px",
                minWidth: "unset !important",
                boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
                maxHeight: "80vh",
                overflowY: "auto",
              }}
              listClassName={classes.listStyle}
              popOverClassName="location-from-overlay"
              unorderedListClassName="lg:w-[414px] md:w-[400px] w-[calc(100vw-80px)] !md:ml-0 !ml-[-1px] !mt-[-4px] !border-border-subdued !rounded-radius-lg
             !px-spacing-m !py-spacing-xs"
            >
              {availableCountries?.map((country) => (
                <DropdownItem value={country.displayName} key={country.code}>
                  <div className="flex gap-spacing-xs items-start leading-[27px] text-[18px] font-bold p-spacing-xs text-text-on-primary">
                    <Image
                      src={country.flag}
                      alt={country.displayName}
                      width={24}
                      height={24}
                    />
                    <span>
                      {country.displayName}

                      {" - "}
                      <span className="font-normal">{country.description}</span>
                    </span>
                  </div>
                </DropdownItem>
              ))}
            </Dropdown>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <UKRedirectModal
        isOpen={showUKModal}
        setIsOpen={setShowUKModal}
        modalHeaderText="Switch to Staysure UK?"
        modalSubText="It looks like you've selected the United Kingdom, so we'll need to take you over to our website for UK residents to make sure you get the right cover for your trip."
        confirmButtonText="Yes, go to Staysure UK"
        cancelButtonText="No, stay on this site"
        onConfirm={handleUKModalConfirm}
        onCancel={handleUKModalCancel}
      />
      <div
        data-testid="question-two-from-test-id"
        className={` rounded-radius-xxl bg-surface-neutral-option-1 rounded-b-radius-xxl pb-spacing-l md:pb-spacing-xxl `}
      >
        <div
          className=" lg:max-w-[414px] md:max-w-[400px] max-w-[unset] w-full mx-auto relative
      flex flex-col justify-between lg:min-h-fit"
        >
          {renderQuestionTwoSection()}
        </div>
      </div>
    </>
  );
}
export default LocationFromContainer;
